/**
 * shareStreakImage.js, Generate a 1080×1080 image celebrating the user's
 * current streak (consecutive days). Drops into Stories/WhatsApp.
 *
 * Visual identity matches the verse share card: navy gradient + gold accents,
 * Fraunces (display) for the number/headline + Manrope (sans) for metadata.
 */

const NAVY = "#0D1B2A";
const NAVY_LIGHT = "#16263A";
const GOLD = "#C9A961";
const PARCHMENT = "#F4ECDC";

const SIZE = 1080;

const fitWidth = (ctx, text, maxWidth, sizes, weight = "400", fam = "Fraunces") => {
  for (const s of sizes) {
    ctx.font = `${weight} ${s}px "${fam}", serif`;
    if (ctx.measureText(text).width <= maxWidth) return s;
  }
  return sizes[sizes.length - 1];
};

const drawCornerOrnament = (ctx, x, y, size, color) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0); ctx.lineTo(size, 0);
  ctx.moveTo(0, 0); ctx.lineTo(0, size);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
};

const drawFlame = (ctx, cx, cy, size, color) => {
  // Stylized flame: two layered teardrops
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(size / 100, size / 100);

  // Outer flame
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -55);
  ctx.bezierCurveTo(45, -25, 35, 25, 18, 45);
  ctx.bezierCurveTo(15, 35, 5, 30, 0, 35);
  ctx.bezierCurveTo(-5, 30, -15, 35, -18, 45);
  ctx.bezierCurveTo(-35, 25, -45, -25, 0, -55);
  ctx.closePath();
  ctx.fill();

  // Inner flame (lighter)
  ctx.fillStyle = "rgba(255, 220, 140, 0.85)";
  ctx.beginPath();
  ctx.moveTo(0, -25);
  ctx.bezierCurveTo(20, -8, 18, 18, 8, 32);
  ctx.bezierCurveTo(5, 24, 0, 22, -2, 28);
  ctx.bezierCurveTo(-12, 18, -18, -8, 0, -25);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
};

/**
 * @param {{
 *   days: number,
 *   tagline?: string,
 *   handle?: string,
 *   url?: string,
 *   brand?: string,
 * }} opts
 */
export const buildStreakCanvas = async ({
  days,
  tagline = days === 1 ? "dia seguido com Deus" : "dias seguidos com Deus",
  handle = "@ccarlosmedeiross",
  url = "app.cemedeiros.com.br",
  brand = "Bíblia com Propósito",
}) => {
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch { /* non-blocking */ }
  }

  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, NAVY);
  grad.addColorStop(1, NAVY_LIGHT);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Gold radial flare
  const flare = ctx.createRadialGradient(SIZE / 2, 380, 80, SIZE / 2, 380, 720);
  flare.addColorStop(0, "rgba(201, 169, 97, 0.30)");
  flare.addColorStop(1, "rgba(201, 169, 97, 0)");
  ctx.fillStyle = flare;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Inner gold frame
  ctx.strokeStyle = "rgba(201, 169, 97, 0.55)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(40, 40, SIZE - 80, SIZE - 80);

  // Corners
  const oSize = 28;
  const oOff = 60;
  drawCornerOrnament(ctx, oOff, oOff, oSize, GOLD);
  ctx.save(); ctx.translate(SIZE - oOff, oOff); ctx.rotate(Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();
  ctx.save(); ctx.translate(SIZE - oOff, SIZE - oOff); ctx.rotate(Math.PI);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();
  ctx.save(); ctx.translate(oOff, SIZE - oOff); ctx.rotate(-Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();

  // Brand
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = GOLD;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(brand.toUpperCase(), SIZE / 2, 140);
  // Tracking divider
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(SIZE / 2 - 30, 170); ctx.lineTo(SIZE / 2 + 30, 170);
  ctx.stroke();

  // Flame icon
  drawFlame(ctx, SIZE / 2, 320, 110, GOLD);

  // Big number
  const numText = String(days);
  const numSize = fitWidth(ctx, numText, 600, [320, 280, 240, 200, 170], "600");
  ctx.font = `600 ${numSize}px "Fraunces", serif`;
  ctx.fillStyle = PARCHMENT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(numText, SIZE / 2, 540);

  // Tagline
  ctx.font = '500 38px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(244, 236, 220, 0.92)";
  ctx.fillText(tagline.toLowerCase(), SIZE / 2, 720);

  // Invitation
  ctx.font = '400 26px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(244, 236, 220, 0.65)";
  ctx.fillText("Junte-se a mim na caminhada", SIZE / 2, 800);

  // Handle
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = GOLD;
  ctx.fillText(handle, SIZE / 2, 900);

  // Domain
  ctx.font = '500 18px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(201, 169, 97, 0.6)";
  ctx.fillText(url, SIZE / 2, 940);

  return canvas;
};

export const buildStreakBlob = async (opts) => {
  const canvas = await buildStreakCanvas(opts);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas toBlob failed"))),
      "image/jpeg",
      0.92,
    );
  });
};
